/**
 * HR Service — API calls for HR-only member data.
 *
 * All endpoints are gated by require_hr() on the backend (403 for non-HR).
 * Salary and national ID are returned decrypted (HR-only); everything else is
 * plaintext. Files are streamed through the backend (the S3 object is ciphertext),
 * so downloads are fetched as authenticated blobs — never a direct URL.
 */

import apiClient from '../lib/axios';
import type {
  MemberHr,
  HrSection,
  MemberChild,
  MemberEvaluation,
  MemberFile,
} from '../types';

/** Section payloads carry only the fields being edited, plus the concurrency token. */
export interface HrSectionPayload {
  expected_updated_at?: string;
  [field: string]: string | number | null | undefined;
}

export interface ChildPayload {
  name: string;
  date_of_birth: string | null;
}

export interface EvaluationPayload {
  evaluation_date: string;
  evaluator_id: number | null;
  strengths: string | null;
  weaknesses: string | null;
  goals: string | null;
  employee_feedback: string | null;
  expected_updated_at?: string;
}

export const hrService = {
  // ── Member HR fields ────────────────────────────────────────────────────────
  getMemberHr: async (memberId: number): Promise<MemberHr> => {
    const { data } = await apiClient.get<MemberHr>(`/member_hr/${memberId}`);
    return data;
  },

  updateSection: async (
    memberId: number,
    section: HrSection,
    payload: HrSectionPayload
  ): Promise<MemberHr> => {
    const { data } = await apiClient.put<MemberHr>(`/member_hr/${memberId}?section=${section}`, payload);
    return data;
  },

  // ── Children ──────────────────────────────────────────────────────────────────
  listChildren: async (memberId: number): Promise<MemberChild[]> => {
    const { data } = await apiClient.get<{ children: MemberChild[] }>(`/member_children/${memberId}`);
    return data.children;
  },

  addChild: async (memberId: number, payload: ChildPayload): Promise<MemberChild> => {
    const { data } = await apiClient.post<MemberChild>(`/member_children/${memberId}`, payload);
    return data;
  },

  updateChild: async (memberId: number, childId: number, payload: ChildPayload): Promise<MemberChild> => {
    const { data } = await apiClient.put<MemberChild>(`/member_children/${memberId}?child_id=${childId}`, payload);
    return data;
  },

  deleteChild: async (memberId: number, childId: number): Promise<void> => {
    await apiClient.delete(`/member_children/${memberId}?child_id=${childId}`);
  },

  // ── Evaluations ─────────────────────────────────────────────────────────────
  listEvaluations: async (memberId: number): Promise<MemberEvaluation[]> => {
    const { data } = await apiClient.get<{ evaluations: MemberEvaluation[] }>(`/member_evaluations/${memberId}`);
    return data.evaluations;
  },

  addEvaluation: async (memberId: number, payload: EvaluationPayload): Promise<MemberEvaluation> => {
    const { data } = await apiClient.post<MemberEvaluation>(`/member_evaluations/${memberId}`, payload);
    return data;
  },

  updateEvaluation: async (
    memberId: number,
    evaluationId: number,
    payload: EvaluationPayload
  ): Promise<MemberEvaluation> => {
    const { data } = await apiClient.put<MemberEvaluation>(
      `/member_evaluations/${memberId}?evaluation_id=${evaluationId}`,
      payload
    );
    return data;
  },

  deleteEvaluation: async (memberId: number, evaluationId: number): Promise<void> => {
    await apiClient.delete(`/member_evaluations/${memberId}?evaluation_id=${evaluationId}`);
  },

  // ── Files ─────────────────────────────────────────────────────────────────────
  listFiles: async (memberId: number): Promise<MemberFile[]> => {
    const { data } = await apiClient.get<{ files: MemberFile[] }>(`/member_files/${memberId}`);
    return data.files;
  },

  uploadFile: async (memberId: number, file: File): Promise<MemberFile> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<MemberFile>(`/member_files/${memberId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /**
   * Download a file as an authenticated blob and trigger a browser save.
   * The object is decrypted server-side and streamed, so we can't use a plain link.
   */
  downloadFile: async (memberId: number, file: MemberFile): Promise<void> => {
    const response = await apiClient.get<Blob>(`/member_files/${memberId}?file_id=${file.id}`, {
      responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = file.file_name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  },

  deleteFile: async (memberId: number, fileId: number): Promise<void> => {
    await apiClient.delete(`/member_files/${memberId}?file_id=${fileId}`);
  },
};
